import React, { memo, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import PageBlock from '@sharedCR/common/PageBlock';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { isFullWidthField, resolveFieldSortRank } from '../../formReview.utils';
import { FORM_REVIEW_LABELS, FormReviewTestIds } from '../../FormReviewCard.constants';
import FormReviewFieldRow from '../FormReviewFieldRow';
import { sectionGridStyles } from '../FormReviewSurface.styles';

const sortByOrder = items => _.sortBy(items ?? [], item => item.order ?? 0);

const EMPTY_TABLE_DIFFS = {};

const FormReviewSection = ({
  section,
  fieldsByKey,
  form,
  tableDiffsByKey = EMPTY_TABLE_DIFFS,
  formId,
  isActionable,
  isInFlight,
  isTriageSorted,
  onOpenDocument,
}) => {
  const schemaFields = useMemo(() => {
    const ordered = sortByOrder(section.fields);

    if (!isTriageSorted) {
      return ordered;
    }

    return _.sortBy(ordered, schemaField =>
      resolveFieldSortRank(fieldsByKey[schemaField.key], form),
    );
  }, [fieldsByKey, form, isTriageSorted, section.fields]);

  return (
    <PageBlock title={section.label ?? section.key}>
      <Box data-testid={FormReviewTestIds.SURFACE_SECTION}>
        {schemaFields.length === 0 ? (
          <Typography color="text.secondary" variant="regular12">
            {FORM_REVIEW_LABELS.EMPTY_SECTION}
          </Typography>
        ) : (
          <Box sx={sectionGridStyles}>
            {schemaFields.map(schemaField => (
              <FormReviewFieldRow
                field={fieldsByKey[schemaField.key]}
                formId={formId}
                isActionable={isActionable}
                isFullWidth={isFullWidthField(schemaField, schemaFields.length)}
                isInFlight={isInFlight}
                key={schemaField.key}
                schemaField={schemaField}
                tableDiff={tableDiffsByKey[schemaField.key] ?? null}
                onOpenDocument={onOpenDocument}
              />
            ))}
          </Box>
        )}
      </Box>
    </PageBlock>
  );
};

FormReviewSection.propTypes = {
  section: PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.string,
    fields: PropTypes.array,
  }).isRequired,
  fieldsByKey: PropTypes.object.isRequired,
  formId: PropTypes.string.isRequired,
  form: PropTypes.object,
  tableDiffsByKey: PropTypes.object,
  isActionable: PropTypes.bool,
  isInFlight: PropTypes.bool,
  isTriageSorted: PropTypes.bool,
  onOpenDocument: PropTypes.func,
};

export default memo(FormReviewSection);
